import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../lib/i18n';
import { ArrowLeft, Home } from 'lucide-react';
import Footer from '../../components/Footer';
import useDocumentTitle from '../../lib/useDocumentTitle';

/**
 * Wrapper for legal pages. Public. Must be reachable without login
 * (the register form has to link to AGB + Datenschutz before the user
 * has an account). Minimal chrome: a back link, the Real Content wordmark,
 * the legal content area, and the shared footer.
 *
 * The content slots in via `children`. Each legal page is otherwise a
 * plain JSX block of section headings + paragraphs that the user
 * replaces with eRecht24-generated content (track B-2).
 */
export default function LegalLayout({ title, children }) {
  const { t } = useTranslation();
  useDocumentTitle(`${title}. Real Content`);
  return (
    <div className="min-h-screen bg-[#f7f3ea]">
      <header className="border-b border-[#e3dccb] backdrop-blur-xl bg-[#f7f3ea]/85">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#4a463b] hover:text-[#23211a]">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">{t('common.back') || 'Zurück'}</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-[#23211a] hover:text-[#23211a]">
            <Home className="w-5 h-5 text-[#1f4d3a]" />
            <span className="font-semibold">Real Content</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-bold text-[#23211a] mb-8">{title}</h1>
        <article className="prose prose-invert prose-slate max-w-none text-[#4a463b] [&_h2]:text-[#1f4d3a] [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-semibold [&_p]:leading-relaxed [&_p]:mb-4 [&_a]:text-[#1f4d3a] [&_a]:underline [&_strong]:text-[#23211a] [&_ul]:my-3 [&_ul]:pl-6 [&_li]:mb-1 [&_code]:text-[#1f4d3a] [&_code]:bg-[#fffdf7] [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded">
          {children}
        </article>
      </main>

      <Footer />
    </div>
  );
}
